import * as Linking from "expo-linking";
import React, { useCallback, useState } from "react";
import { Platform } from "react-native";
import tw from "twrnc";

import { useAuth } from "../../AuthContext";
import { eventLivePageBase } from "../../globalUtils";
import EventSettingsScreenContainer from "@/components/EventSettingsScreenContainer";
import Button, { ButtonRow, CopyButton } from "@/components/settings/Button";
import CodeBox from "@/components/settings/CodeBox";
import { TextInputBox } from "@/components/settings/Field";
import { ChoiceGroup } from "@/components/settings/SettingRow";
import {
  SettingsPage,
  SettingsSection,
} from "@/components/settings/SettingsPage";
import { previewLanguage, t } from "@/localization";

const { videoWallEmbedCode } = require("../../shared/embedCodes.cjs");

// Profile › My Video Wall: evt.live/streams/<username>, the page with the
// broadcaster's public and ticketed events (eventlive-domain
// utils/videoWall.mjs decides which), and its embed code.
const VideoWall = () => {
  const { broadcaster } = useAuth();
  const [codeType, setCodeType] = useState("script");

  const videoWallLink = `${eventLivePageBase}streams/${broadcaster}`;

  // Opened to look at it, so in the app's language; the link to copy has none.
  const openWall = useCallback(() => {
    const lang = previewLanguage();
    const url = lang
      ? `${videoWallLink}?lang=${encodeURIComponent(lang)}`
      : videoWallLink;
    if (Platform.OS === "web") window.open(url, "_blank", "noopener");
    else Linking.openURL(url);
  }, [videoWallLink]);

  const embedCode = videoWallEmbedCode({
    base: eventLivePageBase,
    broadcaster,
    type: codeType,
  });

  return (
    <EventSettingsScreenContainer fallbackRoute="Profile">
      <SettingsPage
        title={t("Video Wall")}
        help="videoWall.overview"
        description={t(
          "One page with your events that anyone with the link can watch, and your ticketed events, newest first."
        )}
      >
        <SettingsSection first title={t("Your Video Wall link")}>
          <TextInputBox
            value={videoWallLink}
            readOnly
            accessibilityLabel={t("Video Wall link")}
            actions={[
              {
                icon: "copy-outline",
                label: t("Copy Video Wall link"),
                copy: videoWallLink,
                toast: t("Link copied"),
                toastDetail: videoWallLink,
              },
            ]}
          />
          <ButtonRow align="start" style={tw`mt-3`}>
            <CopyButton
              value={videoWallLink}
              text={t("Copy link")}
              toast={t("Link copied")}
              toastDetail={videoWallLink}
              size="md"
              fullWidth={false}
            />
            <Button
              text={t("Open Video Wall")}
              icon="open-outline"
              size="md"
              fullWidth={false}
              onPress={openWall}
            />
          </ButtonRow>
        </SettingsSection>

        <SettingsSection
          title={t("Embed on your website")}
          help="videoWall.embed"
          description={t(
            "Paste the code into your website's HTML or embed block."
          )}
        >
          <ChoiceGroup
            accessibilityLabel={t("Code type")}
            value={codeType}
            onChange={setCodeType}
            style={tw`mb-3`}
            options={[
              {
                value: "script",
                label: t("Script (recommended)"),
                description: t(
                  "The wall grows to fit all your events instead of scrolling inside a box."
                ),
              },
              {
                value: "iframe",
                label: t("Simple iframe (no script)"),
                description: t(
                  "For site builders that remove scripts. The wall scrolls inside the space your page gives it."
                ),
              },
            ]}
          />
          <CodeBox code={embedCode} />
          <CopyButton
            value={embedCode}
            text={t("Copy embed code")}
            copiedText={t("Embed code copied")}
            toast={t("Embed code copied")}
            toastDetail={t("Paste it into your website's HTML.")}
            variant="primary"
          />
        </SettingsSection>
      </SettingsPage>
    </EventSettingsScreenContainer>
  );
};

export default VideoWall;
